import { useEffect, useMemo } from 'react';
import { AppLayout } from '@/layout/AppLayout';
import { applyRouteMeta } from '@/lib/meta';
import type { RouteDefinition } from '@/routes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EditorialMetaCard } from '@/components/content/EditorialMetaCard';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { legacyGuideArticles } from '@/content/legacy-guide-articles';
import { getEditorialMetadata } from '@/content/editorial-metadata';
import { cn } from '@/lib/utils';

type Props = {
  route: RouteDefinition;
};

type GuideTocEntry = {
  id: string;
  label: string;
};

type GuideBlock = {
  id?: string;
  title?: string;
  html: string;
};

type ParsedGuide = {
  eyebrow: string;
  title: string;
  intro: string;
  toc: GuideTocEntry[];
  blocks: GuideBlock[];
};

function normalizeGuideHref(rawHref: string): string {
  const href = rawHref.trim();

  if (!href || href.startsWith('#') || href.startsWith('//') || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href)) {
    return href;
  }

  const match = href.match(/^([^?#]+)([?#].*)?$/);
  const pathPart = match?.[1] ?? href;
  const suffix = match?.[2] ?? '';

  let normalizedPath = pathPart.replace(/^(\.\.\/|\.\/)+/, '');
  if (normalizedPath.endsWith('.html')) {
    normalizedPath = normalizedPath.slice(0, -'.html'.length);
  }

  if (!normalizedPath.startsWith('/')) {
    normalizedPath = `/${normalizedPath}`;
  }

  return `${normalizedPath}${suffix}`;
}

function getElementChildren(element: Element): HTMLElement[] {
  return Array.from(element.children).filter((child): child is HTMLElement => child instanceof HTMLElement);
}

function stripAttributes(root: HTMLElement) {
  const nodes: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];

  for (const node of nodes) {
    const tagName = node.tagName.toLowerCase();
    if (tagName === 'script' || tagName === 'style') {
      node.remove();
      continue;
    }

    for (const { name } of Array.from(node.attributes)) {
      if (name === 'class' || name === 'style' || name.startsWith('on')) {
        node.removeAttribute(name);
      }
    }

    if (tagName === 'a') {
      const href = node.getAttribute('href');
      if (!href) {
        continue;
      }

      node.setAttribute('href', normalizeGuideHref(href));
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noreferrer');
      }
    }
  }
}

function stripComments(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Comment[] = [];

  while (walker.nextNode()) {
    comments.push(walker.currentNode as Comment);
  }

  for (const comment of comments) {
    comment.remove();
  }
}

function decorateGuideMarkup(root: HTMLElement) {
  for (const wrapper of Array.from(root.querySelectorAll<HTMLElement>('div'))) {
    const children = getElementChildren(wrapper);
    if (!children.length) {
      continue;
    }

    const childDivs = children.filter((child) => child.tagName === 'DIV');
    const hasTable = children.some((child) => child.tagName === 'TABLE');

    if (hasTable) {
      wrapper.classList.add('guide-table-wrap');
    }

    if (childDivs.length >= 2 && childDivs.length === children.length) {
      wrapper.classList.add('guide-grid');
      if (childDivs.length === 2) {
        wrapper.classList.add('guide-grid-2');
      } else if (childDivs.length === 3) {
        wrapper.classList.add('guide-grid-3');
      }

      for (const childDiv of childDivs) {
        childDiv.classList.add('guide-panel');
      }

      continue;
    }

    const hasSubheading = children.some((child) => child.tagName === 'H3');
    const looksLikeSimpleCallout =
      children.length === 1 &&
      children[0].tagName === 'P' &&
      wrapper.querySelector('strong') !== null;

    if (looksLikeSimpleCallout) {
      wrapper.classList.add('guide-note');
      continue;
    }

    if (hasSubheading && !wrapper.closest('table')) {
      wrapper.classList.add('guide-panel');
    }
  }
}

function parseGuide(markup: string, fallbackTitle: string, fallbackIntro: string): ParsedGuide | null {
  if (typeof DOMParser === 'undefined') {
    return null;
  }

  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const article = doc.querySelector('article');
  if (!article) {
    return null;
  }

  const topLevelDivs = Array.from(article.children).filter(
    (child): child is HTMLDivElement => child instanceof HTMLDivElement
  );

  const hero = topLevelDivs[0] ?? null;
  const tocContainer = topLevelDivs.find((div) => div.querySelector('a[href^="#"]')) ?? null;
  const contentContainer = topLevelDivs[topLevelDivs.length - 1] ?? null;

  const heroParagraphs = hero ? Array.from(hero.querySelectorAll(':scope > p')) : [];
  const eyebrow = heroParagraphs[0]?.textContent?.trim() || 'Guide';
  const title = hero?.querySelector(':scope > h1')?.textContent?.trim() || fallbackTitle;
  const intro = heroParagraphs[heroParagraphs.length - 1]?.textContent?.trim() || fallbackIntro;

  const tocEntries: GuideTocEntry[] = [];
  if (tocContainer) {
    for (const anchor of Array.from(tocContainer.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'))) {
      const href = anchor.getAttribute('href') ?? '';
      const id = href.replace(/^#/, '').trim();
      const label = anchor.textContent?.trim() ?? '';
      if (!id || !label) {
        continue;
      }
      if (tocEntries.some((entry) => entry.id === id)) {
        continue;
      }
      tocEntries.push({ id, label });
    }
  }

  const blocks: GuideBlock[] = [];
  if (contentContainer) {
    for (const rawBlock of Array.from(contentContainer.children)) {
      if (!(rawBlock instanceof HTMLElement)) {
        continue;
      }

      const block = rawBlock.cloneNode(true) as HTMLElement;
      stripComments(block);

      const id = block.getAttribute('id')?.trim() || undefined;
      const directHeading = block.querySelector(':scope > h2');
      const titleText = directHeading?.textContent?.trim() || undefined;
      if (directHeading) {
        directHeading.remove();
      }

      stripAttributes(block);
      decorateGuideMarkup(block);

      const html = block.innerHTML.trim();
      if (!html && !titleText) {
        continue;
      }

      blocks.push({
        id,
        title: titleText,
        html
      });
    }
  }

  return {
    eyebrow,
    title,
    intro,
    toc: tocEntries,
    blocks
  };
}

export function LegacyGuidePage({ route }: Props) {
  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const guideMarkup = legacyGuideArticles[route.canonicalPath];
  const editorialMetadata = useMemo(() => getEditorialMetadata(route.canonicalPath), [route.canonicalPath]);
  const parsedGuide = useMemo(
    () => (guideMarkup ? parseGuide(guideMarkup, route.title, route.description) : null),
    [guideMarkup, route.description, route.title]
  );

  if (!guideMarkup || !parsedGuide) {
    return null;
  }

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {parsedGuide.eyebrow}
        </p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">{parsedGuide.title}</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">{parsedGuide.intro}</p>
      </section>

      {editorialMetadata ? <EditorialMetaCard metadata={editorialMetadata} /> : null}

      {parsedGuide.toc.length > 0 ? (
        <section className="brand-frame rounded-2xl border border-border bg-card/85 p-5 md:p-6">
          <h2 className="font-display text-2xl leading-tight text-foreground">In This Guide</h2>
          <div className="mt-4 grid gap-2 md:grid-cols-2">
            {parsedGuide.toc.map((entry) => (
              <a key={entry.id} href={`#${entry.id}`} className="guide-jump-link">
                {entry.label}
              </a>
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid gap-4">
        {parsedGuide.blocks.map((block, index) => (
          <Card
            key={`${block.id ?? block.title ?? 'block'}-${index}`}
            id={block.id}
            className="brand-frame scroll-mt-24 border-border/80 bg-card/92"
          >
            {block.title ? (
              <CardHeader>
                <CardTitle className="font-display text-2xl leading-tight text-foreground">
                  {block.title}
                </CardTitle>
              </CardHeader>
            ) : null}
            <CardContent className={cn('guide-prose', block.title ? '' : 'pt-6')}>
              <div dangerouslySetInnerHTML={{ __html: block.html }} />
            </CardContent>
          </Card>
        ))}
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
